import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheAlert } from 'app/modules/alerts/queries/useCacheAlert';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconSearch } from '@u21/tabler-icons';

// Selectors
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatAlertOptions } from 'app/modules/search/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { AlertSearchPayload } from 'app/modules/search/payload';
import {
  searchAlert,
  useAlertSearch,
} from 'app/modules/alerts/queries/useAlertSearch';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { getAlertSearchConditions } from 'app/modules/search/utils';
import { AlertSearchOptions, MatchType } from 'app/modules/search/enum';

export type U21AlertSearchProps<TClearable extends boolean> = DistributiveOmit<
  U21SelectSearchProps<number, TClearable>,
  'options' | 'onSearch'
>;

export const U21AlertSearch = <TClearable extends boolean>(
  props: U21AlertSearchProps<TClearable>,
) => {
  const { multi, value } = props;

  const queryClient = useQueryClient();

  const { mutate: cacheAlert } = useCacheAlert();

  const cachePrefetch = useCallback(
    (newValues: number[]) => {
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((alertId) => {
        if (prevSet.has(alertId)) return;
        cacheAlert({ alert_id: alertId });
      });
    },
    [cacheAlert, value],
  );

  const [payload, setPayload] = useState<AlertSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const hasReadAlertsPermissions = useSelector(selectHasReadAlertsPermission);

  const {
    alerts: data,
    query: { isFetching: loading, isError },
    refreshAlerts,
  } = useAlertSearch(payload);

  const options = useMemo(() => formatAlertOptions(data), [data]);

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (hasReadAlertsPermissions) {
      let missingValues: number[] = [];

      if (Array.isArray(value)) {
        missingValues = value.filter(
          (i) => !options.find((j) => j.value === i),
        );
      } else if (value !== undefined) {
        const valueExists = options.find((i) => i.value === value);
        if (!valueExists) {
          missingValues = [value];
        }
      }

      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;

        const initialPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: [
            {
              field: AlertSearchOptions.ID,
              match_type: MatchType.EXACT,
              phrases: missingValues.map(String),
            },
          ],
        };

        queryClient.prefetchQuery({
          queryKey: ALERT_QUERY_KEYS.getAlertSearch(initialPayload),
          queryFn: () => searchAlert(initialPayload),
        });

        setPayload(initialPayload);
      }
    }
  }, [hasReadAlertsPermissions, options, queryClient, value]);

  const onSearch = useCallback((query?: string) => {
    setSearchValue(query || '');

    if (query) {
      setPayload({
        offset: OFFSET_DEFAULT,
        limit: INFINITE_SCROLL_LIMIT,
        search_conditions: getAlertSearchConditions([query]),
      });
    }
  }, []);

  if (!hasReadAlertsPermissions) {
    return null;
  }

  let noOptionsText = 'Type to search or paste a list of alert IDs';
  if (isError) {
    noOptionsText = 'Search failed. Please try again.';
  } else if (searchValue) {
    noOptionsText = 'No results found';
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<number, TClearable>,
    | 'loading'
    | 'onExactSearch'
    | 'onSearch'
    | 'options'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading,
    onExactSearch: async (searchTerms: string[]) => {
      const searchPayload = {
        offset: OFFSET_DEFAULT,
        limit: searchTerms.length,
        search_conditions: [
          {
            field: AlertSearchOptions.ID,
            match_type: MatchType.EXACT,
            phrases: searchTerms,
          },
        ],
      };

      await queryClient.prefetchQuery({
        gcTime: Infinity,
        queryKey: ALERT_QUERY_KEYS.getAlertSearch(searchPayload),
        queryFn: () => searchAlert(searchPayload),
      });
      refreshAlerts();
    },
    onSearch,
    options,
    startIcon: <IconSearch />,
    noOptionsText,
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<number, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<number, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        if (newValue !== undefined && newValue !== null) {
          cachePrefetch([newValue]);
        }
        onChange(newValue, e);
      }}
    />
  );
};
